import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, CornerDownLeft, ArrowUp, ArrowDown } from 'lucide-react';
import { T } from '../adminTheme';
import { NAV_FLAT } from '../adminNav';

// Command palette / quick-nav — lists every sidebar page, searchable, with
// full keyboard support (type to filter, ↑↓ to move, Enter to go, Esc to close).
const CommandPalette = ({ open, onClose }) => {
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const inputRef = useRef(null);

    const results = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return NAV_FLAT;
        return NAV_FLAT.filter(item =>
            item.label.toLowerCase().includes(q) || (item.group || '').toLowerCase().includes(q)
        );
    }, [query]);

    useEffect(() => {
        if (open) {
            setQuery('');
            setActiveIndex(0);
            setTimeout(() => inputRef.current?.focus(), 10);
        }
    }, [open]);

    useEffect(() => { setActiveIndex(0); }, [query]);

    const go = (item) => {
        if (!item) return;
        navigate(item.path);
        onClose();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') { onClose(); return; }
        if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex(i => Math.min(i + 1, results.length - 1)); return; }
        if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex(i => Math.max(i - 1, 0)); return; }
        if (e.key === 'Enter') { e.preventDefault(); go(results[activeIndex]); }
    };

    if (!open) return null;

    return (
        <div style={styles.overlay} onClick={onClose}>
            <div style={styles.modal} onClick={e => e.stopPropagation()} onKeyDown={handleKeyDown}>
                <div style={styles.searchRow}>
                    <Search size={18} color={T.textMuted} />
                    <input
                        ref={inputRef}
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        placeholder="Search pages…"
                        style={styles.input}
                    />
                    <span style={styles.escChip}>Esc</span>
                </div>

                <div style={styles.resultsWrap}>
                    {results.length === 0 && (
                        <div style={styles.empty}>No pages match "{query}"</div>
                    )}
                    {results.map((item, i) => (
                        <div
                            key={item.path}
                            style={{ ...styles.resultRow, ...(i === activeIndex ? styles.resultRowActive : {}) }}
                            onMouseEnter={() => setActiveIndex(i)}
                            onClick={() => go(item)}>
                            <div style={styles.resultIcon}><item.Icon size={16} color={i === activeIndex ? T.primary : T.textSecondary} /></div>
                            <span style={styles.resultLabel}>{item.label}</span>
                            {item.group && <span style={styles.resultGroup}>{item.group}</span>}
                        </div>
                    ))}
                </div>

                <div style={styles.footer}>
                    <span style={styles.hint}><ArrowUp size={12} /><ArrowDown size={12} /> Navigate</span>
                    <span style={styles.hint}><CornerDownLeft size={12} /> Select</span>
                    <span style={{ ...styles.hint, marginLeft: 'auto' }}>Esc Close</span>
                </div>
            </div>
        </div>
    );
};

const styles = {
    overlay: {
        position: 'fixed', inset: 0, background: 'var(--admin-overlay)',
        zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '10vh 16px',
    },
    modal: {
        width: '100%', maxWidth: '560px', background: T.sidebarBg, borderRadius: '16px',
        boxShadow: 'var(--admin-panel-shadow)', overflow: 'hidden',
        display: 'flex', flexDirection: 'column', maxHeight: '70vh',
    },
    searchRow: { display: 'flex', alignItems: 'center', gap: '10px', padding: '16px 18px', borderBottom: `1px solid ${T.border}`, flexShrink: 0 },
    input: { flex: 1, border: 'none', outline: 'none', fontSize: '15px', color: T.textPrimary, background: 'transparent', fontFamily: 'inherit' },
    escChip: { fontSize: '11px', fontWeight: '600', color: T.textMuted, border: `1px solid ${T.border}`, borderRadius: '6px', padding: '3px 7px' },
    resultsWrap: { overflowY: 'auto', padding: '8px' },
    empty: { padding: '24px', textAlign: 'center', fontSize: '13px', color: T.textMuted },
    resultRow: {
        display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px',
        borderRadius: '10px', cursor: 'pointer',
    },
    resultRowActive: { background: T.primaryPale },
    resultIcon: { width: '30px', height: '30px', borderRadius: '8px', background: T.iconBadgeBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    resultLabel: { fontSize: '13.5px', fontWeight: '600', color: T.textPrimary, flex: 1 },
    resultGroup: { fontSize: '11.5px', color: T.textMuted },
    footer: { display: 'flex', alignItems: 'center', gap: '18px', padding: '10px 18px', borderTop: `1px solid ${T.border}`, flexShrink: 0 },
    hint: { display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11.5px', color: T.textMuted },
};

export default CommandPalette;
